<template>
    <div class="portrait-menu-wrapper">
        <!-- Top Section: Language Flags + Search -->
        <div class="flex justify-between th-shadow mb-6 bg-base">
            
            <!-- Language Flags (horizontal) - Portrait only, NOT mobile -->
            <div v-if="!mobile" class="basis-1/3 th-border border-r px-24 py-18 flex gap-8 items-center justify-center">
                <button
                    v-for="lang in dropdownLanguages"
                    :key="lang.code"
                    @click="setLanguage(lang)"
                    class="flag-button"
                    :class="{ active: lang.code === currentLanguageCode }"
                    :title="lang.label"
                >
                    <span
                        class="flag-icon"
                        :style="{ backgroundImage: 'url(' + lang.flag + ')' }"
                    ></span>
                </button>
            </div>

<!-- Original shortcuts section - Mobile only -->
<div v-if="mobile" class="basis-1/3 th-border border-r px-24 py-18 hidden md:flex gap-12">
    <WFShortcutsMenu class="th-btn-group th-btn-group-row" :showLabel="false"></WFShortcutsMenu>
</div>

            <!-- Search Bar -->
<div class="basis-2/3 grow relative px-24 py-18" v-touch:tap="showSearch()">
    <input
        type="text"
        id="search-output"
        :class="[
            'th-shadow border p-12 px-24 shrink rounded-sm mr-2 transition-width w-full text-18',
            mobile ? 'h-40' : 'h-64'
        ]"
    />
    <div :class="['p-18 absolute right-24', mobile ? 'top-8' : 'top-18']">
        <WFIcon
            name="search"
            :class="mobile ? 'w-28 h-26' : 'w-32 h-32'"
            v-show="!searchVisible"
        ></WFIcon>
        <WFIcon
            name="close"
            class="w-32 h-32"
            v-show="searchVisible"
        ></WFIcon>
    </div>
</div>
        </div>

        <!-- Category Chips Section - Only show on small screens (mobile) -->
        <div v-if="portrait" class="category-chips-wrapper px-24 mb-6 md:hidden">
            <!-- USE MOCK DATA FOR LOCALHOST TESTING -->
            <div v-if="useMockData" class="mobile-groups-menu">
                <div class="wf-list">
                    <button
                        v-for="group in mockGroups"
                        :key="group.id"
                        @click="setMockGroup(group)"
                        :class="['wf-list-item', { 'wf-active': currentMockGroup && currentMockGroup.id === group.id }]"
                    >
                        <div class="wf-item-content">{{ group.name }}</div>
                    </button>
                </div>
            </div>

            <!-- REAL WAYFINDER GROUPS (PRODUCTION) -->
            <WFGroupsMenu
                v-else
                class="mobile-groups-menu"
                :currentGroup="currentGroup"
                @clicked="setGroup"
                :order="true"
                :showSubGroups="false"
            >
            </WFGroupsMenu>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";

export default {
    components: {
        
    },
    data() {
        return {
            // MOCK DATA FOR LOCALHOST TESTING - REMOVE IN PRODUCTION
            useMockData: false,
            currentMockGroup: null,
            mockGroups: [
                { id: 1, name: 'Service og kontorer' },
                { id: 2, name: 'Velvære' },
                { id: 3, name: 'Hjemme- og sportsartikler' },
                { id: 4, name: 'Kosmetikk og apotek' },
                { id: 5, name: 'Kaféer og restauranter' },
                { id: 6, name: 'Møte og tilbehør' },
                { id: 7, name: 'Spesial butikker' },
                { id: 8, name: 'Minibank' },
            ],
            
            // Language data
            currentLanguageCode: "no",
            fallbackFlags: {
                no: "https://flagcdn.com/no.svg",
                en: "https://flagcdn.com/gb.svg",
                sv: "https://flagcdn.com/se.svg",
            },
            fallbackLanguages: [
                { code: "no", label: "Norsk" },
                { code: "en", label: "English" },
                { code: "sv", label: "Svenska" },
            ],
        }
    },
    computed: {
        ...mapState(["searchVisible", "currentGroup"]),
        ...mapState("wf", ["portrait", "mobile"]),
        ...mapState({
            storeLanguages: (state) =>
                (state.wf && Array.isArray(state.wf.languages)
                    ? state.wf.languages
                    : []) || [],
            storeLanguageCode: (state) =>
                state.wf && state.wf.language ? state.wf.language : null,
        }),
        
        dropdownLanguages() {
            const fromStore =
                this.storeLanguages && this.storeLanguages.length
                    ? this.storeLanguages
                        .map((lang) => this.mapStoreLanguage(lang))
                        .filter((lang) => !!lang)
                    : [];

            const merged = new Map();

            this.fallbackLanguages.forEach((fallback) => {
                merged.set(fallback.code, {
                    ...fallback,
                    flag: this.fallbackFlags[fallback.code] || null,
                });
            });

            fromStore.forEach((lang) => {
                merged.set(lang.code, {
                    ...merged.get(lang.code),
                    ...lang,
                    flag: lang.flag || this.fallbackFlags[lang.code],
                });
            });

            return Array.from(merged.values());
        },
    },
    mounted() {
        const saved = localStorage.getItem("selectedLanguage");
        if (saved) {
            this.currentLanguageCode = saved.toLowerCase();
        } else if (this.storeLanguageCode) {
            this.currentLanguageCode = this.storeLanguageCode.toLowerCase();
        }
    },
    watch: {
        storeLanguageCode: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.currentLanguageCode = newVal.toLowerCase();
                }
            },
        },
    },
    methods: {
        showSearch() {
            return () => {
                this.$store.dispatch("showSearch", !this.searchVisible);
                this.$store.dispatch("setGroup", null);
                if (this.useMockData) {
                    this.currentMockGroup = null;
                }
            };
        },
        setGroup(group) {
            this.$store.dispatch("setGroup", group);
            this.$store.dispatch("showSearch", false);
        },
        setMockGroup(group) {
            this.currentMockGroup = group;
            console.log('Mock group selected:', group.name);
        },
        
        // Language methods
        mapStoreLanguage(lang) {
            const rawCode =
                (typeof lang.getName === "function" && lang.getName()) ||
                lang.name ||
                lang.code;

            if (!rawCode) return null;

            const code = rawCode.toLowerCase();
            const label =
                (typeof lang.getNativeName === "function" && lang.getNativeName()) ||
                lang.label ||
                lang.name ||
                rawCode.toUpperCase();

            return {
                code,
                label,
                flag: this.fallbackFlags[code] || null,
            };
        },
        
        setLanguage(lang) {
            if (!lang) return;

            const codeLower = (lang.code || "").toLowerCase();
            this.currentLanguageCode = codeLower;
            localStorage.setItem("selectedLanguage", codeLower);

            // Call Wayfinder setLanguage
            const wfInstance = this.$wf || this.$wayfinder || window.wayfinder;
            if (wfInstance && typeof wfInstance.setLanguage === "function") {
                wfInstance.setLanguage(codeLower);
            }
        },
    },
}
</script>

<style lang="postcss" scoped>
    input {
        border-color: var(--th-secondary-content);
    }

    /* Flag buttons styling */
    .flag-button {
        width: 6.6rem;
        height: 5rem;
        padding: 0.4rem;
        border-radius: 0.6rem;
        background-color: transparent;
        border: 2px solid transparent;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
    }

    .flag-button:hover {
        border-color: var(--th-primary);
    }

    .flag-button.active {
        border-color: transparent;
        background-color: transparent;
        transform: scale(1.25);
    }

    .flag-button.active .flag-icon {
        box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);
    }

    .flag-icon {
        display: block;
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center;
        border-radius: 0.4rem;
        transition: all 0.2s ease;
    }

    /* Category Chips Styling for Mobile */
    .category-chips-wrapper {
        overflow: hidden;
    }

    .mobile-groups-menu {
        padding: 0 !important;
        background: transparent !important;
    }

    .mobile-groups-menu :deep(.wf-list) {
        display: flex !important;
        flex-direction: row !important;
        gap: 0.875rem;
        overflow-x: auto;
        overflow-y: hidden;
        padding-bottom: 0.75rem;
        padding-top: 0.25rem;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        flex-wrap: nowrap !important;
    }

    .mobile-groups-menu :deep(.wf-list)::-webkit-scrollbar {
        display: none;
    }

    .mobile-groups-menu :deep(.wf-list-item) {
    flex-shrink: 0 !important;
    padding: 1rem 1.75rem !important;
    border-radius: 14px !important;
    font-size: 1.0625rem !important;
    font-weight: 600 !important;
    white-space: nowrap !important;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
    cursor: pointer !important;
    background-color: var(--color-base-100);
    color: var(--color-base-content) !important;
    border: 2px solid #d1d5db !important;
    height: auto !important;
    min-height: 48px !important;
    min-width: auto !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
    letter-spacing: 0.01em !important;
}

.mobile-groups-menu :deep(.wf-list-item:hover) {
    background-color: var(--color-base-200);
    border-color: var(--color-primary) !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12) !important;
    transform: translateY(-1px) !important;
}

.mobile-groups-menu :deep(.wf-list-item.wf-active) {
    background-color: var(--color-primary) !important;
    color: var(--color-primary-content) !important;
    border-color: var(--color-primary) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
    transform: translateY(-1px) !important;
}

    .mobile-groups-menu :deep(.wf-item-content) {
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        width: 100% !important;
        height: 100% !important;
    }

    @media (max-width: 768px) {
        .category-chips-wrapper {
            padding-left: 1rem;
            padding-right: 1rem;
        }

        .mobile-groups-menu :deep(.wf-list) {
            gap: 0.75rem;
        }

        .mobile-groups-menu :deep(.wf-list-item) {
            padding: 0.875rem 1.5rem !important;
            font-size: 1.5rem !important;
            border-radius: 12px !important;
            min-height: 44px !important;
        }
    }
</style>